import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import * as React from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { v4 as uuid } from 'uuid';
import { IState } from '..';
import { Button } from '../../../../components-v2/Button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../../components-v2/Select';
import { TextInput } from '../../../../components/TextInput';
import { Toggle } from '../../../../components/Toggle';
import { grayIconFill } from '../../../../styles/colors';
import { FormFieldWrapper } from '../../shared/components/FormFieldWrapper';
import { styleSheet } from '../styles';
import { EventRegistrationFormData } from '../utils';
import { NotificationOptions } from './index';
import { TicketCard } from './TicketCard';

interface PaymentsSettingsProps {
	state: {
		enableRequestAttendeesToRSVP: boolean;
		now: Date;
		selectedImage: Api.IFileAttachment | null;
	};
	eventData?: Api.IEventRegistrationSurvey;
	account?: Api.IAccount;
	publishableApiKeys?: Api.IStripeKeys;
	onToggleCheckChanged: () => void;
	onClickMedia: () => void;
	onClickFreeImage: () => void;
	isClone?: boolean;
	setState: (state: React.Dispatch<React.SetStateAction<IState>>) => void;
}

const paidEventOptions = [
	{ value: 'Paid Event', label: 'Paid Event' },
	{ value: 'Free Event', label: 'Free Event' },
];

const ticketSortModeOptions = [
	{ value: Api.TicketSortMode.PriceAscending, label: 'Lowest price first' },
	{ value: Api.TicketSortMode.PriceDescending, label: 'Highest price first' },
	{ value: Api.TicketSortMode.NameAscending, label: 'Name: A–Z' },
	{ value: Api.TicketSortMode.NameDescending, label: 'Name: Z–A' },
];

export const PaymentsSettings: React.FC<PaymentsSettingsProps> = ({
	eventData,
	account,
	publishableApiKeys,
	isClone,
}) => {
	const { control, watch, setValue, formState } = useFormContext<EventRegistrationFormData>();
	const formValues = watch();

	// Track original ticket IDs from backend (only for existing events, not clones)
	const originalTicketIds = React.useMemo(() => {
		if (!eventData?._type || isClone) return new Set<string>();
		const ids = eventData?.attendeeOptions?.eventTickets?.map(t => t.id).filter(Boolean) || [];
		return new Set(ids as string[]);
	}, [eventData, isClone]);

	// Determine if this is a legacy paid event (has existing event data with legacy cost, no tickets)
	const isLegacyPaidEvent = Boolean(
		eventData?._type &&
			eventData?.isPaidEvent &&
			!isClone &&
			eventData?.attendeeOptions?.costPerAttendee &&
			eventData?.attendeeOptions?.costPerAttendee > 0 &&
			(!eventData?.attendeeOptions?.eventTickets || eventData?.attendeeOptions?.eventTickets.length === 0)
	);

	// Handler for deleting a ticket
	const handleDeleteTicket = React.useCallback(
		(index: number) => {
			const newTickets = formValues.eventTickets?.filter((_, i) => i !== index) || [];
			setValue('eventTickets', newTickets);
		},
		[formValues.eventTickets, setValue]
	);

	// Ensure at least one ticket exists when isPaidEvent is enabled
	React.useEffect(() => {
		if (
			formValues.isPaidEvent &&
			(!formValues.eventTickets || formValues.eventTickets.length === 0) &&
			!isLegacyPaidEvent
		) {
			const initialTicket: Api.IEventTicket = {
				id: uuid(),
				name: '',
				description: '',
				unitPrice: 0,
				maxAllowed: null,
				attendeeCountPerItem: 1,
				isDefault: false,
				hideTicket: false,
				showAsSoldOut: false,
			};
			setValue('eventTickets', [initialTicket]);
		}
	}, [formValues.isPaidEvent, formValues.eventTickets, isLegacyPaidEvent, setValue]);

	return (
		<section className={css(styleSheet.section)}>
			<h3 className={css(styleSheet.sectionTitle)}>
				{account?.features?.stripePayment?.enabled && Boolean(publishableApiKeys)
					? 'Payments & Notifications'
					: 'Notifications'}
			</h3>
			{account?.features?.stripePayment?.enabled && Boolean(publishableApiKeys) ? (
				<>
					{/* Show "Set Event Cost" toggle ONLY for legacy paid events (has cost but no tickets) */}
					{isLegacyPaidEvent && (
						<div className={css(styleSheet.formControlWrap)}>
							<Controller
								name='isPaidEvent'
								control={control}
								render={({ field: { ref, ...fieldProps } }) => (
									<Toggle
										id='set-event-cost-checkbox'
										isOn={fieldProps.value}
										disabled={Boolean(eventData?._type) && eventData?.isPaidEvent && !isClone}
										onToggleCheckChanged={() => {
											fieldProps.onChange(!fieldProps.value);
										}}
										text='Set Event Cost'
										textStyles={[styleSheet.toggleText]}
										uncheckedColor={grayIconFill}
									/>
								)}
							/>
						</div>
					)}

					{/* Legacy cost input - only for legacy paid events */}
					{isLegacyPaidEvent && formValues.isPaidEvent ? (
						<div className={css(styleSheet.formControlWrap)}>
							<Controller
								name='payments.cost'
								control={control}
								render={({ field: { ref, ...fieldProps } }) => {
									return (
										<TextInput
											id='event-cost-input'
											inputId='event-cost-input'
											leftAccessory={<span>$</span>}
											onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
												const val = e.target.value;

												if (isNaN(Number(val))) {
													return;
												}

												if (Number(val) < 0 || val === '') {
													fieldProps.onChange(0);
													return;
												}

												fieldProps.onChange(Number(e.target.value));
											}}
											disabled={Boolean(eventData?._type) && eventData?.isPaidEvent && !isClone}
											type='text'
											autoFocus
											value={fieldProps.value}
										/>
									);
								}}
							/>

							{formState.isSubmitted && formState.errors.payments?.cost && (
								<p className={css(styleSheet.errorMessage, styleSheet.noMargin)}>
									{formState.errors.payments.cost.message}
								</p>
							)}
						</div>
					) : null}

					{/* Tiered Tickets Section - Show for new surveys or non-legacy events */}
					{!isLegacyPaidEvent && (
						<>
							{/* Show "Tiered Tickets" dropdown */}
							{account?.features?.stripePayment?.enabled && Boolean(publishableApiKeys) && (
								<FormFieldWrapper
									name='isPaidEvent'
									control={control}
									title='Tiered Tickets'
									description='Enable the ability to sell tiers of tickets for this event.'
									controllerWrapperStyles={[styleSheet.controllerWrapper]}
								>
									{({ value, onChange }) => (
										<Select
											value={value ? 'Paid Event' : 'Free Event'}
											onValueChange={val => {
												const isPaidEvent = val === 'Paid Event';
												onChange(isPaidEvent);

												// When switching to paid event, disable maximum capacity
												if (isPaidEvent) {
													setValue('requireMaximumCapacity', false);
												}
											}}
											disabled={Boolean(eventData?._type) && eventData?.isPaidEvent && !isClone}
										>
											<SelectTrigger>
												<SelectValue />
											</SelectTrigger>
											<SelectContent>
												{paidEventOptions.map(option => (
													<SelectItem key={option.value} value={option.value}>
														{option.label}
													</SelectItem>
												))}
											</SelectContent>
										</Select>
									)}
								</FormFieldWrapper>
							)}

							{/* Tiered tickets UI */}
							{formValues.isPaidEvent ? (
								<div className={css(styleSheet.formControlWrap)}>
									<FormFieldWrapper
										name='ticketSortMode'
										control={control}
										title='Ticket display order'
										description='How tiers are ordered on the event registration page'
										controllerWrapperStyles={[styleSheet.controllerWrapper]}
									>
										{({ value, onChange }) => {
											const selectedMode = value || Api.TicketSortMode.PriceAscending;
											return (
												<Select value={selectedMode} onValueChange={val => onChange(val as Api.TicketSortMode)}>
													<SelectTrigger>
														<SelectValue>
															{ticketSortModeOptions.find(option => option.value === selectedMode)?.label}
														</SelectValue>
													</SelectTrigger>
													<SelectContent>
														{ticketSortModeOptions.map(option => (
															<SelectItem key={option.value} value={option.value}>
																{option.label}
															</SelectItem>
														))}
													</SelectContent>
												</Select>
											);
										}}
									</FormFieldWrapper>

									{formValues.eventTickets?.map((ticket, index) => {
										// Only disable price for tickets that came from the backend (Stripe limitation)
										const isPriceDisabled = ticket.id ? originalTicketIds.has(ticket.id) : false;

										return (
											<TicketCard
												key={`${ticket?.id}-${index}`}
												index={index}
												control={control}
												formState={formState}
												isPriceDisabled={isPriceDisabled}
												onDelete={
													(formValues.eventTickets?.length || 0) > 1 && !isPriceDisabled
														? () => handleDeleteTicket(index)
														: undefined
												}
											/>
										);
									})}

									<Button
										type='button'
										variant='link'
										onClick={() => {
											const newTicket: Api.IEventTicket = {
												id: uuid(),
												name: '',
												description: '',
												unitPrice: 0,
												maxAllowed: null,
												attendeeCountPerItem: 1,
												isDefault: false,
												hideTicket: false,
												showAsSoldOut: false,
											};
											setValue('eventTickets', [...(formValues.eventTickets || []), newTicket]);
										}}
									>
										+ Add Ticket Tier
									</Button>

									{formState.isSubmitted && formState.errors.eventTickets && (
										<p className={css(styleSheet.errorMessage, styleSheet.noMargin)}>
											{formState.errors.payments?.cost?.message || 'At least one ticket is required for paid events'}
										</p>
									)}
								</div>
							) : null}
						</>
					)}
				</>
			) : null}
			<NotificationOptions survey={eventData} />
		</section>
	);
};
